import type { FormEvent, JSX } from 'react';
import { useTranslation } from 'react-i18next';

import type { LoginErrorKind } from '@/lib/auth/loginError';
import { formValue } from '@/lib/forms';

import { TotpStep } from './TotpStep';

/**
 * BP-01 §6 `staff /login`: "email+password (+TOTP step)", states "idle,
 * submitting, error (invalid, locked, readonly banner)".
 *
 * One form, three fields, the third revealed only once the api has refused the
 * first two — see `lib/auth/loginError.ts` for why a refusal cannot say whether
 * it was the password or the missing code.
 */

/** The values one submission carries. */
export interface LoginValues {
  email: string;
  password: string;
  /** Empty when the TOTP step is not showing, or showing and left blank. */
  totp: string;
}

export function LoginForm({
  status,
  errorKind,
  showTotp,
  onSubmit,
}: {
  status: 'idle' | 'submitting';
  errorKind: LoginErrorKind | null;
  /** Reveal the one-time code field (`TotpStep`). */
  showTotp: boolean;
  onSubmit: (values: LoginValues) => void;
}): JSX.Element {
  const { t } = useTranslation('auth');
  const submitting = status === 'submitting';

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    onSubmit({
      email: formValue(data, 'email'),
      password: formValue(data, 'password'),
      totp: formValue(data, 'totp'),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form" noValidate>
      <h1 className="text-xl font-semibold">{t('login.title')}</h1>

      {errorKind === null ? null : (
        <p role="alert" data-testid="login-error" className="rounded bg-status-no-show p-3 text-destructive">
          {t(`error.${errorKind}`)}
        </p>
      )}

      <label className="block">
        <span className="block text-sm text-muted-foreground">{t('login.email')}</span>
        <input
          name="email"
          type="email"
          autoComplete="username"
          required
          className="mt-1 w-full rounded border border-border p-2"
        />
      </label>

      <label className="block">
        <span className="block text-sm text-muted-foreground">{t('login.password')}</span>
        <input
          name="password"
          type="password"
          autoComplete="current-password"
          required
          className="mt-1 w-full rounded border border-border p-2"
        />
      </label>

      {showTotp ? <TotpStep /> : null}

      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded bg-primary p-2 text-primary-foreground disabled:opacity-60"
      >
        {submitting ? t('login.submitting') : t('login.submit')}
      </button>
    </form>
  );
}
